import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface LeagueSeasonState {
    currentSeason: string;
    currentLeagueId: string;
    setCurrentSeason: (season: string) => void;
    setCurrentLeagueId: (leagueId: string) => void;
    ensureSeason: (availableSeasons: string[], preferred?: string) => void;
    ensureLeague: (availableLeagueIds: string[], preferred?: string) => void;
    reset: () => void;
}

const normalizeList = (values: string[]): string[] => {
    return Array.from(
        new Set(
            values
                .map((value) => value?.trim())
                .filter((value): value is string => Boolean(value))
        )
    );
};

export const useLeagueSeasonStore = create<LeagueSeasonState>()(
    persist(
        (set, get) => ({
            currentSeason: "",
            currentLeagueId: "",
            setCurrentSeason: (season) =>
                set((state) =>
                    state.currentSeason === season
                        ? state
                        : { currentSeason: season ?? "" }
                ),
            setCurrentLeagueId: (leagueId) =>
                set((state) =>
                    state.currentLeagueId === leagueId
                        ? state
                        : { currentLeagueId: leagueId ?? "" }
                ),
            ensureSeason: (availableSeasons, preferred) => {
                const sanitized = normalizeList(availableSeasons);
                console.log('[useLeagueSeasonStore] ensureSeason called:', {
                    availableSeasons,
                    preferred,
                    sanitized,
                    currentSeason: get().currentSeason
                });
                if (!sanitized.length) {
                    console.warn('[useLeagueSeasonStore] No seasons available, clearing currentSeason');
                    if (get().currentSeason) {
                        set({ currentSeason: "" });
                    }
                    return;
                }
                const { currentSeason } = get();
                if (sanitized.includes(currentSeason)) {
                    console.log('[useLeagueSeasonStore] Current season is valid, keeping it:', currentSeason);
                    return;
                }
                const fallback =
                    (preferred && sanitized.includes(preferred) && preferred) ||
                    sanitized[0];
                console.log('[useLeagueSeasonStore] Setting fallback season:', fallback);
                set({ currentSeason: fallback });
            },
            ensureLeague: (availableLeagueIds, preferred) => {
                const sanitized = normalizeList(availableLeagueIds);
                console.log('[useLeagueSeasonStore] ensureLeague called:', {
                    availableLeagueIds,
                    preferred,
                    sanitized,
                    currentLeagueId: get().currentLeagueId
                });
                if (!sanitized.length) {
                    console.warn('[useLeagueSeasonStore] No leagues available, clearing currentLeagueId');
                    if (get().currentLeagueId) {
                        set({ currentLeagueId: "" });
                    }
                    return;
                }
                const { currentLeagueId } = get();
                if (sanitized.includes(currentLeagueId)) {
                    console.log('[useLeagueSeasonStore] Current league is valid, keeping it:', currentLeagueId);
                    return;
                }
                const fallback =
                    (preferred && sanitized.includes(preferred) && preferred) ||
                    sanitized[0];
                console.log('[useLeagueSeasonStore] Setting fallback league:', fallback);
                set({ currentLeagueId: fallback });
            },
            reset: () => set({ currentSeason: "", currentLeagueId: "" }),
        }),
        {
            name: "league-season-store",
            partialize: (state) => ({
                currentSeason: state.currentSeason,
                currentLeagueId: state.currentLeagueId,
            }),
        }
    )
);

export const getLeagueSeasonState = () => useLeagueSeasonStore.getState();
